import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FileText, Users, AlertTriangle,
  Settings, Menu, X, TrendingUp, LogOut, Shield,
} from 'lucide-react';
import { documentsApi, rgpdApi, documentTypesApi } from '../api';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useSidebarResize } from '../hooks/useSidebarResize';
import { ChatbotWidget } from './ChatbotWidget';

const BRAND = '#0BBCC1';

// ── Sidebar ───────────────────────────────────────────────────────

function Sidebar({ isOpen, onClose, width }: { isOpen: boolean; onClose: () => void; width: number }) {
  const [docCount, setDocCount]                   = useState<number | null>(null);
  const [alertCount, setAlertCount]               = useState<number | null>(null);
  const [autoTypeCount, setAutoTypeCount]         = useState<number | null>(null);
  const user     = useCurrentUser();
  const navigate = useNavigate();
  const isDev    = user?.email === 'admin@acodoc.dev';

  const refreshCounts = () => {
    documentsApi.list({ size: 1 })
      .then(r => setDocCount(r.data.total))
      .catch(() => setDocCount(null));

    rgpdApi.listAlerts({ size: 1, status: 'active' })
      .then(r => {
        const total = r.data.total;
        setAlertCount(total > 0 ? total : null);
      })
      .catch(() => setAlertCount(null));

    documentTypesApi.list()
      .then(r => {
        const count = r.data.filter(t => t.auto_created).length;
        setAutoTypeCount(count > 0 ? count : null);
      })
      .catch(() => setAutoTypeCount(null));
  };

  useEffect(() => {
    refreshCounts();
    const interval = setInterval(refreshCounts, 5_000);
    window.addEventListener('acodoc:refresh-sidebar', refreshCounts);
    return () => {
      clearInterval(interval);
      window.removeEventListener('acodoc:refresh-sidebar', refreshCounts);
    };
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('acodoc_token');
    navigate('/login');
  };

  const MAIN_NAV = [
    { to: '/dashboard',      label: 'Tableau de bord', icon: LayoutDashboard },
    { to: '/documents',      label: 'Documents',        icon: FileText,      badge: docCount,   badgeColor: BRAND },
    { to: '/collaborateurs', label: 'Collaborateurs',   icon: Users },
    { to: '/rgpd',           label: 'Alertes RGPD',     icon: AlertTriangle, badge: alertCount, badgeColor: '#ef4444' },
    { to: '/stats',          label: 'Statistiques',     icon: TrendingUp },
  ];

  const navItem = (item: typeof MAIN_NAV[0], onClick: () => void) => {
    const Icon = item.icon;
    return (
      <NavLink key={item.to} to={item.to} onClick={onClick}>
        {({ isActive }) => (
          <span
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
              isActive
                ? ''
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
            style={isActive ? {
              background: `${BRAND}15`,
              color: BRAND,
              borderLeft: `2px solid ${BRAND}`,
              paddingLeft: '10px',
            } : undefined}
          >
            <Icon className="w-[17px] h-[17px] shrink-0" />
            <span className="flex-1 text-left">{item.label}</span>
            {item.badge != null && item.badge > 0 && (
              <span
                className={`text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center leading-none ${item.to === '/rgpd' ? 'animate-pulse' : ''}`}
                style={{ background: item.badgeColor }}
              >
                {item.badge > 999 ? '999+' : item.badge}
              </span>
            )}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <>
      {/* Backdrop mobile */}
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={onClose} />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-60 flex flex-col overflow-hidden
          transition-transform duration-200
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:static lg:translate-x-0 lg:shrink-0
        `}
        style={{ background: '#060d16', borderRight: '1px solid rgba(255,255,255,0.04)', width }}
      >
        {/* ── Fond dot grid ───────────────────────────────────────── */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle, rgba(11,188,193,0.07) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }} />

        {/* ── Orb teal ambient ────────────────────────────────────── */}
        <div className="absolute -top-16 -left-16 w-56 h-56 rounded-full pointer-events-none" style={{
          background: 'radial-gradient(circle, rgba(11,188,193,0.18) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }} />
        <div className="absolute bottom-0 right-0 w-40 h-40 rounded-full pointer-events-none" style={{
          background: 'radial-gradient(circle, rgba(11,188,193,0.08) 0%, transparent 70%)',
          filter: 'blur(30px)',
        }} />

        {/* ── Contenu (relatif pour passer au-dessus du fond) ─────── */}
        <div className="relative flex flex-col h-full">

          {/* Bouton fermeture mobile */}
          <button
            onClick={onClose}
            className="lg:hidden absolute top-3 right-3 p-1.5 hover:bg-white/10 rounded-lg text-white/50 hover:text-white z-10"
          >
            <X className="w-4 h-4" />
          </button>

          {/* ── Logo + brand header ─────────────────────────────── */}
          <div className="px-5 pt-7 pb-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            {/* Logo direct sur fond sombre */}
            <div className="flex justify-center mb-4">
              <img
                src="/assets/ACO_All_Black_Top.png"
                alt="Alcyone Consulting"
                className="h-30 w-auto object-contain select-none pointer-events-none"
                style={{ filter: 'brightness(0) invert(1) drop-shadow(0 0 14px rgba(11,188,193,0.45))' }}
                draggable={false}
                onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            </div>

            {/* ACODOC — séparateur lignes + label (non interactif) */}
            <div className="flex items-center gap-2.5">
              <div className="h-px flex-1" style={{ background: `linear-gradient(to right, transparent, ${BRAND}40)` }} />
              <span className="text-[11px] font-bold tracking-[0.18em] uppercase select-none" style={{ color: BRAND }}>
                ACODOC
              </span>
              <div className="h-px flex-1" style={{ background: `linear-gradient(to left, transparent, ${BRAND}40)` }} />
            </div>
          </div>

          {/* ── Navigation ──────────────────────────────────────── */}
          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            <p className="text-[9px] font-bold tracking-[0.16em] uppercase px-3 mb-2" style={{ color: 'rgba(255,255,255,0.2)' }}>
              Navigation
            </p>
            {MAIN_NAV.map(item => navItem(item, onClose))}

            <div className="my-3" style={{ height: '1px', background: 'rgba(255,255,255,0.05)' }} />

            <p className="text-[9px] font-bold tracking-[0.16em] uppercase px-3 mb-2" style={{ color: 'rgba(255,255,255,0.2)' }}>
              Système
            </p>
            <NavLink to="/settings" onClick={onClose}>
              {({ isActive }) => (
                <span
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                    isActive ? '' : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }`}
                  style={isActive ? { background: `${BRAND}15`, color: BRAND, borderLeft: `2px solid ${BRAND}`, paddingLeft: '10px' } : undefined}
                  title={autoTypeCount ? `${autoTypeCount} type(s) de document à configurer (durée RGPD)` : undefined}
                >
                  <Settings className="w-[17px] h-[17px] shrink-0" />
                  <span className="flex-1 text-left">Paramètres</span>
                  {autoTypeCount != null && autoTypeCount > 0 && (
                    <span
                      className="text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center leading-none animate-pulse"
                      style={{ background: '#f59e0b' }}
                    >
                      {autoTypeCount > 999 ? '999+' : autoTypeCount}
                    </span>
                  )}
                </span>
              )}
            </NavLink>
          </nav>

          {/* ── Profil utilisateur ──────────────────────────────── */}
          <div className="px-3 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            <div className="flex items-center gap-3 px-2 py-2.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
              {/* Avatar + infos cliquables → paramètres utilisateur */}
              <NavLink
                to="/settings"
                onClick={onClose}
                className="flex items-center gap-3 flex-1 min-w-0 rounded-lg transition hover:opacity-80"
                title="Modifier mon profil"
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0"
                  style={{
                    background: isDev ? 'rgba(251,191,36,0.15)' : `${BRAND}20`,
                    border: isDev ? '1px solid rgba(251,191,36,0.35)' : `1px solid ${BRAND}35`,
                    color: isDev ? 'rgba(251,191,36,0.9)' : BRAND,
                  }}
                >
                  {user?.initials ?? '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: 'rgba(255,255,255,0.75)' }}>
                    {user?.full_name || 'Utilisateur'}
                  </p>
                  <p className="text-[10px] truncate" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    {isDev ? 'Mode développement' : user?.email}
                  </p>
                </div>
              </NavLink>

              {/* Logout */}
              <button
                onClick={handleLogout}
                title="Se déconnecter"
                className="p-1.5 rounded-lg transition shrink-0"
                style={{ color: 'rgba(255,255,255,0.3)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#ef4444'; (e.currentTarget as HTMLElement).style.background = 'rgba(239,68,68,0.1)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.3)'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Ligne de conformité RGPD en bas */}
            <div className="flex items-center gap-1.5 mt-2.5 px-2">
              <Shield className="w-3 h-3 shrink-0" style={{ color: `${BRAND}60` }} />
              <span className="text-[9px] tracking-wide" style={{ color: 'rgba(255,255,255,0.15)' }}>
                Souverain · Données internes · RGPD
              </span>
            </div>
          </div>

        </div>
      </aside>
    </>
  );
}

// ── Layout principal ──────────────────────────────────────────────

export function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { width: sidebarWidth, handleMouseDown, isResizing } = useSidebarResize('main-sidebar-width', 240, 'right');

  return (
    <div className="flex h-screen bg-gray-50 font-sans antialiased overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} width={sidebarWidth} />

      {/* Handle resize — bord droit du sidebar, desktop uniquement */}
      <div
        onMouseDown={handleMouseDown}
        className="hidden lg:flex w-1.5 items-center justify-center cursor-col-resize group flex-shrink-0 z-10"
        style={{ background: isResizing ? 'rgba(11,188,193,0.08)' : undefined }}
        title="Redimensionner le menu"
      >
        <div className={`w-0.5 h-10 rounded-full transition-colors ${isResizing ? 'bg-teal-400' : 'bg-white/10 group-hover:bg-teal-400'}`} />
      </div>

      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Barre mobile hamburger */}
        <div className="lg:hidden h-14 bg-white border-b border-gray-200 flex items-center px-4 gap-3 shrink-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
          <span className="font-bold text-sm tracking-widest uppercase" style={{ color: BRAND }}>
            ACODOC
          </span>
        </div>
        <Outlet />
      </main>
      <ChatbotWidget />
    </div>
  );
}
